import {
  SEASONAL_EVIDENCE_MAX_MONTH,
  SEASONAL_EVIDENCE_MIN_MONTH,
  type SeasonalEvidenceRecord,
} from "./schema";

/**
 * NL-010 curated-source boundary.
 *
 * This module deliberately ships with no source records. A future owner-approved
 * integration must pass a curated registry explicitly; this resolver neither
 * fetches data nor treats a record as licensed or fresh. The calculation engine
 * remains responsible for validating that candidate with its caller-supplied
 * `asOfDate`.
 */
export const SEASONAL_SOURCE_REGISTRY_VERSION = "nl010-source-registry-v1";

/**
 * The repository default is intentionally empty. Keeping this immutable makes
 * an omitted registry fail closed without inventing an approved market record.
 */
export const DEFAULT_SEASONAL_EVIDENCE_REGISTRY: readonly SeasonalEvidenceRecord[] = Object.freeze([]);

export type SeasonalEvidenceRegistry = readonly unknown[];

export type SeasonalEvidenceResolutionInput = {
  /** Omit to use the intentionally empty repository default. */
  readonly records?: SeasonalEvidenceRegistry;
  readonly geography?: string;
  readonly serviceCategory?: string;
  /**
   * The calendar month (1–12) being planned. Records are scoped by month, so a
   * full market is twelve records under one `(geography, serviceCategory)` pair
   * that differ only by month; the resolver selects the one for this month.
   */
  readonly plannedMonth?: number;
};

export type SeasonalEvidenceResolutionBlocker =
  | "missing-requested-geography"
  | "missing-requested-service-category"
  | "missing-requested-month"
  | "invalid-requested-month"
  | "invalid-source-registry"
  | "missing-evidence"
  | "ambiguous-evidence";

export type SeasonalEvidenceResolutionResult = {
  readonly registryVersion: typeof SEASONAL_SOURCE_REGISTRY_VERSION;
  readonly readiness: "ready" | "blocked";
  readonly blockers: readonly SeasonalEvidenceResolutionBlocker[];
  /**
   * This is an unvalidated candidate. Consumers must pass it to
   * `validateSeasonalEvidence` before using an index.
   */
  readonly evidence: unknown | null;
  readonly matchingRecordCount: number;
};

type UnknownRecord = Readonly<Record<string, unknown>>;

function asRecord(value: unknown): UnknownRecord | null {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as UnknownRecord)
    : null;
}

function stableIdentifier(value: unknown): string | null {
  if (typeof value !== "string") return null;

  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function isPlannedMonth(value: unknown): value is number {
  return (
    typeof value === "number" &&
    Number.isInteger(value) &&
    value >= SEASONAL_EVIDENCE_MIN_MONTH &&
    value <= SEASONAL_EVIDENCE_MAX_MONTH
  );
}

function matchesRequestedScope(
  record: unknown,
  geography: string,
  serviceCategory: string,
  plannedMonth: number,
): boolean {
  const candidate = asRecord(record);

  return (
    stableIdentifier(candidate?.geography) === geography &&
    stableIdentifier(candidate?.serviceCategory) === serviceCategory &&
    (isPlannedMonth(candidate?.plannedMonth) ? candidate?.plannedMonth : null) === plannedMonth
  );
}

/**
 * Resolves exactly one caller-supplied record for a stable
 * geography/category/month scope. Twelve monthly records for one market coexist
 * because the month is part of the key; only a duplicate of the *same* month is
 * ambiguous. The resolver performs no clock or network I/O and never mutates the
 * supplied registry or its records.
 */
export function resolveSeasonalEvidence(input: unknown): SeasonalEvidenceResolutionResult {
  const candidate = asRecord(input);
  const geography = stableIdentifier(candidate?.geography);
  const serviceCategory = stableIdentifier(candidate?.serviceCategory);
  const requestedMonth = candidate?.plannedMonth;
  const blockers: SeasonalEvidenceResolutionBlocker[] = [];

  if (!geography) blockers.push("missing-requested-geography");
  if (!serviceCategory) blockers.push("missing-requested-service-category");
  if (requestedMonth === undefined || requestedMonth === null) {
    blockers.push("missing-requested-month");
  } else if (!isPlannedMonth(requestedMonth)) {
    blockers.push("invalid-requested-month");
  }

  const suppliedRecords = candidate?.records;
  const records = suppliedRecords === undefined ? DEFAULT_SEASONAL_EVIDENCE_REGISTRY : suppliedRecords;

  if (!Array.isArray(records)) {
    blockers.push("invalid-source-registry");
  }

  const matches =
    geography && serviceCategory && isPlannedMonth(requestedMonth) && Array.isArray(records)
      ? records.filter((record) => matchesRequestedScope(record, geography, serviceCategory, requestedMonth))
      : [];

  if (blockers.length === 0 && matches.length === 0) blockers.push("missing-evidence");
  if (matches.length > 1) blockers.push("ambiguous-evidence");

  return {
    registryVersion: SEASONAL_SOURCE_REGISTRY_VERSION,
    readiness: blockers.length === 0 ? "ready" : "blocked",
    blockers,
    evidence: blockers.length === 0 ? matches[0] : null,
    matchingRecordCount: matches.length,
  };
}
